import React, { useMemo } from 'react';
import { useHistory, useRouteMatch } from '@shared/core';
import { createContext } from '@shared/utils/createContext';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { OccasionName } from '@apps/imagineYourCard/Components/ImagineYourCardRoot/hooks/useImagineOptions';
import { CardFormat } from './routes/Dashboard/components/ProductDetailsPage/constants';

export const CARDS_BASE_PATH = '/cards';

const createPaths = (
  path: string | undefined,
  options: {
    public: boolean;
    admin: boolean;
  },
) => {
  const paths = [];

  if (options.public) {
    paths.push(`${CARDS_BASE_PATH}${path}`);
  }

  if (options.admin) {
    paths.push(`/:eventHandle/edit${CARDS_BASE_PATH}${path}`);
  }

  return { paths };
};

const CARD_ROUTES = {
  dashboard: createPaths('', { public: true, admin: true }),
  imagine: createPaths('/:occasion/:category/imagine/', { public: true, admin: true }),
  designsGallery: createPaths('/:occasion/:category', { public: true, admin: true }),
  samplePack: createPaths('/sample-pack', { public: false, admin: true }),
  samplePackSuccess: createPaths('/sample-pack/success', { public: false, admin: true }),
  drafts: createPaths('/drafts', { public: false, admin: true }),
  favorites: createPaths('/favorites', { public: false, admin: true }),
  /**
   * @deprecated use `productDetailsPageV2` instead
   */
  productDetailsPage: createPaths('/:occasion/:category/product/:themeId', { public: true, admin: true }),
  productDetailsPageV2: createPaths('/:occasion/:category/product/:themeId/:templateId', { public: true, admin: true }),
  paperCardCustomizer: createPaths('/customize/:cardDraftId', { public: false, admin: true }),
  digitalCardCustomizer: createPaths('/digital/customize/:cardDraftId', { public: false, admin: true }),

  editOrder: createPaths('/orders/:printOrderNumber/customize', { public: false, admin: true }),
  editDigitalOrder: createPaths('/orders/:printOrderNumber/digital/customize', { public: false, admin: true }),
  orderHistory: createPaths('/orders', { public: false, admin: true }),
  orderDetails: createPaths('/orders/:printOrderNumber', { public: false, admin: true }),
  orderTracking: createPaths('/orders/:printOrderNumber/tracking', { public: false, admin: true }),

  designConcierge: createPaths('/concierge', { public: false, admin: true }),
} as const;

type CardsRouterContext = {
  getCustomizeDraftPath: (cardDraftId: string) => string;
  getConciergePath: (setName: string) => string;
  getOrderDetailsPath: (printOrderNumber: string) => string;
  getOrderTrackingPath: (printOrderNumber: string) => string;
  getCustomizeOrderDetailsPath: (printOrderNumber: string) => string;
  getCustomizeOrderDetailsPathAtRecipientsStep: (printOrderNumber: string) => string;
  getOrdersPath: () => string;
  getDraftsPath: () => string;
  getFavoritesPath: () => string;
  getCardImaginePath: (occasion?: OccasionName, category?: StationeryTemplateCategoryEnum) => string;
  getDigitalCustomizeDraftPath: (cardDraftId: string) => string;
  getDigitalOrderPostCheckoutPath: (cardDraftId: string, orderNumber: string) => string;
  getShopPath: (occasion: 'wedding', category?: StationeryTemplateCategoryEnum) => string;
  getProductDetailsPagePath: (occasion: 'wedding', category: StationeryTemplateCategoryEnum, themeId: string, templateId: string, format?: CardFormat) => string;
  getPublicRootPath: () => string;
  getSamplePackPath: () => string;
  getSamplePackPostCheckoutPath: () => string;

  goToCustomizeDraft: (draftId: string) => void;
  goToConcierge: (setName: string) => void;
  goToEventDashboard: () => void;
  goToOrderDetails: (orderNumber: string) => void;
  goToCustomizeOrderDetails: (orderNumber: string) => void;
  goToCustomizeDigitalOrderDetails: (orderNumber: string) => void;
  goToCustomizeOrderDetailsAtRecipientsStep: (orderNumber: string) => void;
  goToCardDrafts: () => void;
  goToOrders: () => void;
  goToDigitalCustomizeDraft: (theme: string) => void;
  goToShop: (occasion: 'wedding', category?: StationeryTemplateCategoryEnum) => void;
  goToProductDetailsPage: (
    occasion: 'wedding',
    category: StationeryTemplateCategoryEnum,
    themeId: string,
    templateId: string,
    action?: 'push' | 'replace',
    format?: CardFormat,
  ) => void;
  goToMessageTrackingPage: (orderNumber: string) => void;
  goToDigitalOrderPostCheckoutPath: (cardDraftId: string, orderNumber: string) => void;
  routes: typeof CARD_ROUTES;
  dayOfStationeryRoutes: string[];

  isPublicShopRoute: boolean;
};

export const NAMED_CATEGORY_TO_STATIONERY_TEMPLATE_ENUM_MAP: Record<string, StationeryTemplateCategoryEnum> = {
  'save-the-date': StationeryTemplateCategoryEnum.saveTheDate,
  invitation: StationeryTemplateCategoryEnum.invitation,
  'thank-you': StationeryTemplateCategoryEnum.thankYou,
  holiday: StationeryTemplateCategoryEnum.holiday,
  enclosure: StationeryTemplateCategoryEnum.enclosure,
  napkin: StationeryTemplateCategoryEnum.napkin,
  menu: StationeryTemplateCategoryEnum.menu,
  sign: StationeryTemplateCategoryEnum.sign,
};

const STATIONERY_TEMPLATE_ENUM_TO_NAMED_CATEGORY_MAP: Record<StationeryTemplateCategoryEnum, string> = {
  [StationeryTemplateCategoryEnum.saveTheDate]: 'save-the-date',
  [StationeryTemplateCategoryEnum.invitation]: 'invitation',
  [StationeryTemplateCategoryEnum.thankYou]: 'thank-you',
  [StationeryTemplateCategoryEnum.holiday]: 'holiday',
  [StationeryTemplateCategoryEnum.enclosure]: 'enclosure',
  [StationeryTemplateCategoryEnum.menu]: 'menu',
  [StationeryTemplateCategoryEnum.napkin]: 'napkin',
  [StationeryTemplateCategoryEnum.sign]: 'sign',
  [StationeryTemplateCategoryEnum.monogram]: 'monogram',
};

export const getNamedCategoryPathFromStationeryTemplateCategory = (category: StationeryTemplateCategoryEnum) => {
  return STATIONERY_TEMPLATE_ENUM_TO_NAMED_CATEGORY_MAP[category];
};

export const getStationeryTemplateCategoryFromNamedCategoryPath = (categoryPath: string) => {
  return NAMED_CATEGORY_TO_STATIONERY_TEMPLATE_ENUM_MAP[categoryPath];
};

type ValidShopPageMatch = {
  occasion: 'wedding';
  category: 'save-the-date' | 'invitation' | 'thank-you' | 'holiday' | 'enclosure';
};

export const isValidShopPageMatch = (matchParams: Record<string, string>): matchParams is ValidShopPageMatch => {
  return matchParams.occasion === 'wedding' && matchParams.category in NAMED_CATEGORY_TO_STATIONERY_TEMPLATE_ENUM_MAP;
};

const [Provider, useCardsRouterContext] = createContext<CardsRouterContext>({ name: 'CardsRouteProvider' });

const CardsRoutesProvider = ({ children, eventHandle }: { eventHandle?: string; children: React.ReactNode }) => {
  const history = useHistory();

  const isPublicShopRoute = !!useRouteMatch('/cards');

  const buildRoute = useEventCallback((path: string = '', basePath: string = CARDS_BASE_PATH) => {
    return eventHandle ? `/${eventHandle}/edit${basePath}${path}` : `${basePath}${path}`;
  });

  const getPublicRootPath = useEventCallback(() => {
    return CARDS_BASE_PATH;
  });

  const getDigitalCustomizeDraftPath = useEventCallback<CardsRouterContext['getDigitalCustomizeDraftPath']>(draftId => {
    return buildRoute(`/digital/customize/${draftId}`);
  });

  const getDigitalOrderPostCheckoutPath = useEventCallback<CardsRouterContext['getDigitalOrderPostCheckoutPath']>((draftId, orderNumber) => {
    return buildRoute(`/digital/customize/${draftId}?step=postCheckout&orderNumber=${orderNumber}`);
  });

  const getOrderDetailsPath = useEventCallback<CardsRouterContext['getOrderDetailsPath']>(printOrderNumber => {
    return buildRoute(`/orders/${printOrderNumber}`);
  });
  const getOrderTrackingPath = useEventCallback<CardsRouterContext['getOrderTrackingPath']>(printOrderNumber => {
    return buildRoute(`/orders/${printOrderNumber}/tracking`);
  });

  const getCustomizeOrderDetailsPath = useEventCallback<CardsRouterContext['getCustomizeOrderDetailsPath']>(printOrderNumber => {
    return buildRoute(`/orders/${printOrderNumber}/customize`);
  });

  const getCustomizeDigitalOrderDetailsPath = useEventCallback<CardsRouterContext['getCustomizeOrderDetailsPath']>(digitalOrderNumber => {
    return buildRoute(`/orders/${digitalOrderNumber}/digital/customize`);
  });

  const getCustomizeOrderDetailsPathAtRecipientsStep = useEventCallback<CardsRouterContext['getCustomizeOrderDetailsPathAtRecipientsStep']>(printOrderNumber => {
    return buildRoute(`/orders/${printOrderNumber}/customize?step=recipients`);
  });

  const getCustomizeDraftPath = useEventCallback<CardsRouterContext['getCustomizeDraftPath']>(cardDraftId => {
    return buildRoute(`/customize/${cardDraftId}`);
  });

  const getConciergePath = useEventCallback<CardsRouterContext['getConciergePath']>(setName => {
    return buildRoute(`/concierge?setName=${setName}`);
  });

  const getCardImaginePath = useEventCallback((occasion: OccasionName = 'wedding', category?: StationeryTemplateCategoryEnum) => {
    const pathCategory = getNamedCategoryPathFromStationeryTemplateCategory(category ?? StationeryTemplateCategoryEnum.saveTheDate);
    return buildRoute(`/${occasion}/${pathCategory}/imagine`);
  });

  const getOrdersPath = useEventCallback(() => {
    return buildRoute('/orders');
  });
  const getDraftsPath = useEventCallback(() => {
    return buildRoute('/drafts');
  });
  const getFavoritesPath = useEventCallback(() => {
    return buildRoute('/favorites');
  });

  const getShopPath = useEventCallback((occasion: 'wedding', category?: StationeryTemplateCategoryEnum) => {
    return buildRoute(`/${occasion}` + (category ? `/${getNamedCategoryPathFromStationeryTemplateCategory(category)}` : ''));
  });

  const getProductDetailsPagePath = useEventCallback((occasion: 'wedding', category: StationeryTemplateCategoryEnum, themeId: string, templateId: string, format?: CardFormat) => {
    return buildRoute(`/${occasion}/${getNamedCategoryPathFromStationeryTemplateCategory(category)}/product/${themeId}/${templateId}` + (format ? `?format=${format}` : ''));
  });

  const getSamplePackPath = useEventCallback(() => {
    return buildRoute('/sample-pack');
  });

  const getSamplePackPostCheckoutPath = useEventCallback(() => {
    return buildRoute('/sample-pack/success');
  });

  // Actions

  const goToDigitalCustomizeDraft = useEventCallback((draftId: string) => {
    history.push(getDigitalCustomizeDraftPath(draftId));
  });

  const goToOrders = useEventCallback(() => {
    history.push(getOrdersPath());
  });

  const goToCustomizeDraft = useEventCallback((stationeryDraftId: string) => {
    history.push(getCustomizeDraftPath(stationeryDraftId));
  });

  const goToConcierge = useEventCallback((setName: string) => {
    history.push(getConciergePath(setName));
  });

  const goToEventDashboard = useEventCallback(() => {
    history.push(eventHandle ? `/${eventHandle}/edit` : '/event/edit');
  });

  const goToOrderDetails = useEventCallback((printOrderNumber: string) => {
    history.push(getOrderDetailsPath(printOrderNumber));
  });

  const goToCustomizeOrderDetails = useEventCallback((printOrderNumber: string) => {
    history.push(getCustomizeOrderDetailsPath(printOrderNumber));
  });

  const goToCustomizeDigitalOrderDetails = useEventCallback((digitalOrderNumber: string) => {
    history.push(getCustomizeDigitalOrderDetailsPath(digitalOrderNumber));
  });

  const goToCustomizeOrderDetailsAtRecipientsStep = useEventCallback((printOrderNumber: string) => {
    history.push(getCustomizeOrderDetailsPathAtRecipientsStep(printOrderNumber));
  });

  const goToCardDrafts = useEventCallback(() => {
    history.push(getDraftsPath());
  });

  const goToShop = useEventCallback((occasion: 'wedding', category?: StationeryTemplateCategoryEnum) => {
    history.push(getShopPath(occasion, category));
  });

  const goToProductDetailsPage = useEventCallback<CardsRouterContext['goToProductDetailsPage']>(
    (occasion, category, themeId, templateId, action = 'push', format = CardFormat.paper) => {
      history[action](getProductDetailsPagePath(occasion, category, themeId, templateId, format));
    },
  );

  const goToDigitalOrderPostCheckoutPath = useEventCallback<CardsRouterContext['goToDigitalOrderPostCheckoutPath']>((draftId, orderNumber) => {
    history.push(getDigitalOrderPostCheckoutPath(draftId, orderNumber));
  });

  const goToMessageTrackingPage = useEventCallback((orderNumber: string) => {
    history.push(getOrderTrackingPath(orderNumber));
  });

  const dayOfStationeryRoutes = useMemo(() => {
    return [
      buildRoute(`/:occasion/${getNamedCategoryPathFromStationeryTemplateCategory(StationeryTemplateCategoryEnum.menu)}`),
      buildRoute(`/:occasion/${getNamedCategoryPathFromStationeryTemplateCategory(StationeryTemplateCategoryEnum.napkin)}`),
      buildRoute(`/:occasion/${getNamedCategoryPathFromStationeryTemplateCategory(StationeryTemplateCategoryEnum.sign)}`),
    ];
  }, [buildRoute]);

  return (
    <Provider
      value={{
        getPublicRootPath,
        getCardImaginePath,
        getDraftsPath,
        getFavoritesPath,
        getOrdersPath,
        getCustomizeDraftPath,
        getConciergePath,
        getOrderDetailsPath,
        getOrderTrackingPath,
        getCustomizeOrderDetailsPath,
        getCustomizeOrderDetailsPathAtRecipientsStep,
        getShopPath,
        getProductDetailsPagePath,
        getDigitalOrderPostCheckoutPath,
        getSamplePackPath,
        getSamplePackPostCheckoutPath,

        goToOrders,
        goToCardDrafts,
        goToEventDashboard,
        goToCustomizeDraft,
        goToConcierge,
        goToOrderDetails,
        goToCustomizeOrderDetails,
        goToCustomizeDigitalOrderDetails,
        goToCustomizeOrderDetailsAtRecipientsStep,
        getDigitalCustomizeDraftPath,
        goToDigitalCustomizeDraft,
        goToShop,
        goToProductDetailsPage,
        goToMessageTrackingPage,
        goToDigitalOrderPostCheckoutPath,

        routes: CARD_ROUTES,
        dayOfStationeryRoutes,
        isPublicShopRoute,
      }}
    >
      {children}
    </Provider>
  );
};

export { CardsRoutesProvider, useCardsRouterContext };

export const getStandalonePathToAdminCardPdp = (eventHandle: string, occasion: 'wedding', category: StationeryTemplateCategoryEnum, themeId: string) => {
  return `/${eventHandle}/edit/cards/${occasion}/${getNamedCategoryPathFromStationeryTemplateCategory(category)}/product/${themeId}`;
};

export const getStandalonePathToAdminCardShop = (eventHandle: string, occasion: 'wedding', themeId?: string) => {
  return `/${eventHandle}/edit/cards/${occasion}${themeId ? `?theme=${themeId}` : ''}`;
};
